/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Alert,
  Input,
  Form,
  Tag,
  Tooltip,
  Checkbox,
  message,
  Radio,
  Space,
  Button,
  Select,
} from 'antd';
import { PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';

import ModalWrapper from './ModalWrapper';
import './settings-modal.scss';
import { verifyFeatureAccess } from '../../global/featureFlags';
import { captureAnalytics } from '../../actions/preload';
import { labelColors } from '../shared/colorSwatches';

export class AddLabel extends Component {
  constructor(props) {
    super(props);
    const {
      label = {}, isAutoLabelingEnabled, isLlmDerivedLabelsEnabled,
    } = props.modalType.data;
    const excludedDocumentCategoryIds = (label.excluded_document_categories || []).map((category) => category.id);
    const autoLabelMode = label.auto_label_mode || (
      isAutoLabelingEnabled && isLlmDerivedLabelsEnabled ? 'ai' : 'keywords'
    );
    this.state = {
      positiveKeywords: [],
      negativeKeywords: [],
      newPositiveKeywordValue: '',
      newNegativeKeywordValue: '',
      positiveInputVisible: false,
      negativeInputVisible: false,
      labelName: label.name || '',
      selectedColorIndex: 0,
      autoLabel: false,
      labelsCanBeAssignedDepartment: label.department_id,
      assignedDepartment: label.department_id || null,
      editingLabelID: '',
      showEditConfirmation: false,
      canManageLabel: true,
      autoLabelMode,
      autoLabelInstructions: label.auto_label_instructions || '',
      autoLabelExclusions: label.auto_label_exclusions || '',
      excludedDocumentCategoryIds,
      exclusionsVisible: !!label.auto_label_exclusions || excludedDocumentCategoryIds.length > 0,
    };
  }

  async componentDidMount() {
    const { modalType } = this.props;
    const { label } = modalType.data;
    const canManageLabel = await verifyFeatureAccess('manage_label');
    if (label.name) {
      const {
        name,
        color,
        positive_keywords: positiveKeywords = [],
        negative_keywords: negativeKeywords = [],
        id,
        auto_label: autoLabel,
        auto_label_mode: autoLabelMode,
        auto_label_instructions: autoLabelInstructions,
        auto_label_exclusions: autoLabelExclusions,
        excluded_document_categories: excludedDocumentCategories = [],
      } = label;
      this.setState({
        editingLabelID: id,
        labelName: name,
        positiveKeywords,
        negativeKeywords,
        autoLabel,
        autoLabelMode: autoLabelMode || 'keywords',
        autoLabelInstructions: autoLabelInstructions || '',
        autoLabelExclusions: autoLabelExclusions || '',
        excludedDocumentCategoryIds: excludedDocumentCategories.map((category) => category.id),
        exclusionsVisible: !!autoLabelExclusions || excludedDocumentCategories.length > 0,
        selectedColorIndex: labelColors.findIndex((colorType) => colorType.color === color) === -1 ? 0 : labelColors.findIndex((colorType) => colorType.color === color),
        canManageLabel,
      });
    } else {
      this.setState({ canManageLabel });
    }
  }

  removeKeyword = (removedKeyword, type) => {
    const editedArray = this.state[type];

    const index = this.state[type].indexOf(removedKeyword);
    editedArray.splice(index, 1);
    this.setState({
      [type]: [...editedArray],
    });
  };

  addNewKeyword = (type) => {
    const {
      positiveKeywords,
      negativeKeywords,
      newNegativeKeywordValue,
      newPositiveKeywordValue,
    } = this.state;
    const isPositiveKeyword = type === 'positiveKeywords';
    const inputToClear = isPositiveKeyword ? 'newPositiveKeywordValue' : 'newNegativeKeywordValue';
    const inputFieldToHide = isPositiveKeyword ? 'positiveInputVisible' : 'negativeInputVisible';
    const newKeywordArray = isPositiveKeyword ? [...positiveKeywords, newPositiveKeywordValue] : [...negativeKeywords, newNegativeKeywordValue];
    this.setState({
      [type]: newKeywordArray,
      [inputToClear]: '',
      [inputFieldToHide]: false,
    });
  };

  buildLabelPayload = () => {
    const {
      labelName,
      selectedColorIndex,
      autoLabel,
      autoLabelMode,
      autoLabelInstructions,
      autoLabelExclusions,
      positiveKeywords,
      negativeKeywords,
      newPositiveKeywordValue,
      newNegativeKeywordValue,
      assignedDepartment,
      excludedDocumentCategoryIds,
    } = this.state;
    const selectedColor = labelColors[selectedColorIndex];
    const pendingPositiveKeywords = newPositiveKeywordValue
      ? [...positiveKeywords, newPositiveKeywordValue]
      : positiveKeywords;
    const pendingNegativeKeywords = newNegativeKeywordValue
      ? [...negativeKeywords, newNegativeKeywordValue]
      : negativeKeywords;
    const allPositiveKeywords = [...new Set(
      pendingPositiveKeywords.flatMap((keyword) => {
        const normalizedKeyword = keyword.trim();
        return normalizedKeyword ? [normalizedKeyword] : [];
      }),
    )];
    const allNegativeKeywords = [...new Set(
      pendingNegativeKeywords.flatMap((keyword) => {
        const normalizedKeyword = keyword.trim();
        return normalizedKeyword ? [normalizedKeyword] : [];
      }),
    )];

    return {
      name: labelName.trim(),
      color: selectedColor.color,
      is_dark_text: selectedColor.darkText,
      auto_label: autoLabel,
      auto_label_mode: autoLabelMode,
      auto_label_instructions: autoLabelInstructions.trim(),
      auto_label_exclusions: autoLabelExclusions.trim(),
      excluded_document_category_ids: excludedDocumentCategoryIds,
      positive_keywords: allPositiveKeywords,
      negative_keywords: allNegativeKeywords,
      department_id: assignedDepartment,
    };
  };

  automaticRuleIsValid = () => {
    const { autoLabel, autoLabelMode, autoLabelInstructions } = this.state;
    return !autoLabel || autoLabelMode !== 'ai' || autoLabelInstructions.trim() !== '';
  };

  upgradeToAiInstructions = () => {
    const { modalType } = this.props;
    const { editingLabelID } = this.state;
    if (!modalType.data.aiConversionSource) {
      captureAnalytics('label_ai_conversion_started', {
        label_id: editingLabelID,
        source: 'label_editor',
      });
    }
    this.setState({ autoLabelMode: 'ai' });
  };

  addLabel = async () => {
    const { createNewLabel, getLabelsByPage } = this.props;
    const { labelName } = this.state;
    if (!labelName || labelName.trim() === '') return;
    if (!this.automaticRuleIsValid()) {
      message.error('Describe which documents should receive this label.');
      return;
    }
    await createNewLabel(this.buildLabelPayload());
    const { error, hideModal, errorCode } = this.props;
    const errorMessageContent = (errorCode === 400) ? 'Error adding label. Duplicate label name already exists.' : 'Error adding label. Please try again.';
    if (!error) {
      hideModal();
      message.success({
        content: 'Label successfully added',
      });
      getLabelsByPage();
    } else {
      hideModal();
      message.error({
        content: errorMessageContent,
      });
      getLabelsByPage();
    }
  };

  editLabel = async () => {
    const { updateLabel, getLabelsByPage } = this.props;
    const {
      labelName, editingLabelID, autoLabel, autoLabelMode,
    } = this.state;
    const { label, aiConversionSource } = this.props.modalType.data;
    if (!labelName || labelName.trim() === '') return;
    if (!this.automaticRuleIsValid()) {
      message.error('Describe which documents should receive this label.');
      return;
    }
    const updateResponse = await updateLabel(editingLabelID, this.buildLabelPayload());
    const { error, hideModal, errorCode } = this.props;
    const errorMessageContent = (errorCode === 400) ? 'Error updating label. Duplicate label name already exists.' : 'Error updating label. Please try again.';
    hideModal();
    if (!error) {
      if (!updateResponse?.error && autoLabel && autoLabelMode === 'ai' && label.auto_label_mode !== 'ai') {
        captureAnalytics('label_ai_conversion_completed', {
          label_id: editingLabelID,
          source: aiConversionSource || 'label_editor',
        });
      }
      message.success({
        content: 'Label successfully updated',
      });
    } else {
      message.error({
        content: errorMessageContent,
      });
    }
    getLabelsByPage();
  };

  isColorSelected = (colorObject) => {
    const { selectedColorIndex, canManageLabel } = this.state;
    const { modalType: { data: { isAdmin } } } = this.props;
    const canSelectColor = (canManageLabel && isAdmin) || !canManageLabel;
    return colorObject.color === labelColors[selectedColorIndex].color && canSelectColor;
  };

  renderModal = () => {
    const { isLoading, modalType } = this.props;
    const {
      isAutoLabelingEnabled,
      isAdmin,
      userDepartments,
      isDepartmentLabelsEnabled,
      isLlmDerivedLabelsEnabled,
      documentCategories,
    } = modalType.data;
    const {
      newPositiveKeywordValue,
      newNegativeKeywordValue,
      negativeInputVisible,
      positiveInputVisible,
      autoLabel,
      autoLabelMode,
      autoLabelInstructions,
      autoLabelExclusions,
      excludedDocumentCategoryIds,
      exclusionsVisible,
      labelsCanBeAssignedDepartment,
      assignedDepartment,
      labelName,
      positiveKeywords,
      negativeKeywords,
      editingLabelID,
      canManageLabel,
    } = this.state;
    // Disable button if manage_label is on and user is not admin
    const disableButton = canManageLabel && !isAdmin;
    const showAiRule = isLlmDerivedLabelsEnabled && autoLabelMode === 'ai';
    if (isLoading) {
      return (<span> Adding new label... </span>);
    }
    return (
      <Form
        layout="vertical"
        onFinish={!editingLabelID && this.addLabel}
        id="addLabel"
      >
        <Form.Item
          hasFeedback
          name="label"
          label="Label Name"
          rules={[
            {
              required: true,
              message: 'Must provide label name',
            },
          ]}
        >
          <Input
            value={labelName}
            defaultValue={labelName}
            onChange={(event) => this.setState({ labelName: event.target.value })}
            disabled={disableButton}
          />
        </Form.Item>
        <Form.Item
          name="Color"
          label="Color"
        >
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {labelColors.map((colorObject, index) => (

              <button
                type="button"
                aria-label={`Select ${colorObject.color}`}
                key={colorObject.color}
                onClick={() => this.setState({ selectedColorIndex: index })}
                disabled={disableButton}
                style={{
                  height: 30,
                  width: 30,
                  borderRadius: 100,
                  borderStyle: 'inset',
                  border: this.isColorSelected(colorObject) ? '3px solid white' : '1px solid #d9d9d9',
                  backgroundColor: colorObject.color,
                  margin: '10px',
                  padding: 0,
                  boxShadow: this.isColorSelected(colorObject) ? '0px 0px 20px #b3b3b3' : '',
                  cursor: disableButton ? 'not-allowed' : 'pointer',
                }}
              />
            ))}
          </div>
        </Form.Item>
        <Form.Item
          name="OCR"
          label={(
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Tooltip placement="left" title="Use these labels to autoscan received documents and have them labeled automatically">
                Autolabel
                <InfoCircleOutlined style={{ paddingLeft: '2px', marginTop: '6px', height: '8px' }} />
              </Tooltip>
              {!isAutoLabelingEnabled
                  && (
                    // eslint-disable-next-line object-curly-newline
                    <p style={{ color: 'red', fontSize: 12, marginLeft: 10, marginBottom: 0 }}>
                      You don't have access to this feature. Contact your admin to upgrade.
                    </p>
                  )}
            </div>
            )}
        >
          <Checkbox
            checked={autoLabel}
            onChange={(event) => this.setState({ autoLabel: event.target.checked })}
            disabled={(disableButton) || !isAutoLabelingEnabled}
          >
            Use label to autolabel documents
          </Checkbox>
        </Form.Item>
        {autoLabel && isAutoLabelingEnabled && showAiRule && (
        <div className="ai-label-rule-editor">
          <div className="ai-label-rule-editor__intro">
            Medsender will review each incoming document and apply this label when it matches your description.
          </div>
          <Form.Item
            required
            label="Apply this label for…"
            validateStatus={autoLabelInstructions.trim() === '' ? 'error' : ''}
            help={autoLabelInstructions.trim() === ''
              ? 'Describe which documents should receive this label.'
              : 'Use plain language, such as “All referrals.”'}
          >
            <Input.TextArea
              value={autoLabelInstructions}
              onChange={(event) => this.setState({ autoLabelInstructions: event.target.value })}
              placeholder="All referrals"
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={2000}
              disabled={disableButton}
            />
          </Form.Item>
          {!exclusionsVisible && (
          <Button
            type="link"
            icon={<PlusOutlined />}
            onClick={() => this.setState({ exclusionsVisible: true })}
            disabled={disableButton}
            className="ai-label-rule-editor__exceptions-toggle"
          >
            Add exceptions
            {' '}
            <span>(optional)</span>
          </Button>
          )}
          {exclusionsVisible && (
          <>
            <Form.Item
              label="Do not apply this label for…"
              help="Optional. Describe documents that may look similar but should not receive this label."
            >
              <Input.TextArea
                value={autoLabelExclusions}
                onChange={(event) => this.setState({ autoLabelExclusions: event.target.value })}
                placeholder="TriWest referral authorizations"
                autoSize={{ minRows: 2, maxRows: 5 }}
                maxLength={2000}
                disabled={disableButton}
              />
            </Form.Item>
            {documentCategories.length > 0 && (
            <Form.Item
              label="Do not apply in these categories"
              help="Optional. Documents in these categories will not be considered for this label."
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                maxTagCount="responsive"
                value={excludedDocumentCategoryIds}
                options={documentCategories
                  .map((category) => ({ label: category.emr_category, value: category.id }))
                  .sort((first, second) => first.label.localeCompare(second.label))}
                onChange={(categoryIds) => this.setState({ excludedDocumentCategoryIds: categoryIds })}
                placeholder="Select categories"
                disabled={disableButton}
                className="ai-label-rule-editor__category-exclusions"
              />
            </Form.Item>
            )}
          </>
          )}
        </div>
        )}
        {autoLabel && isAutoLabelingEnabled && autoLabelMode === 'ai' && !isLlmDerivedLabelsEnabled && (
        <Alert
          type="info"
          showIcon
          className="ai-label-unavailable"
          message="This AI rule is preserved"
          description="AI rule settings are no longer available for your organization. You can save other label changes or turn off automatic labeling."
        />
        )}
        {autoLabel && isAutoLabelingEnabled && autoLabelMode !== 'ai' && (
        <div>
          {isLlmDerivedLabelsEnabled && (
          <Alert
            type="info"
            showIcon
            className="ai-label-upgrade"
            message="This label uses keyword matching"
            description={(
              <div>
                <p>Keep this rule as-is, or upgrade it to describe what should receive the label in plain language.</p>
                <Button
                  type="primary"
                  size="small"
                  onClick={this.upgradeToAiInstructions}
                  disabled={disableButton}
                >
                  Upgrade to AI instructions
                </Button>
              </div>
                  )}
          />
          )}
          <Form.Item
            name="positiveKeywords"
            label="Positive Keywords"
          >
            {[...new Set(positiveKeywords)].map((keyword) => (
              <Tag
                color="blue"
                key={`positive-${keyword}`}
                style={{ marginTop: '10px' }}
                closable={true}
                onClose={() => this.removeKeyword(keyword, 'positiveKeywords')}
              >
                {keyword}
              </Tag>
            ))}
            {positiveInputVisible && (
            <Input
              ref={(positiveInput) => { if (positiveInput != null) { positiveInput.focus(); } }}
              name="positiveKeywordInput"
              type="text"
              size="small"
              style={{ width: '45%', marginTop: '5px' }}
              value={newPositiveKeywordValue}
              onChange={(e) => this.setState({ newPositiveKeywordValue: e.target.value })}
              onPressEnter={() => this.addNewKeyword('positiveKeywords')}
              disabled={disableButton}
            />
            )}
            {!positiveInputVisible && (
            <Tag style={{ marginTop: '10px' }} onClick={() => this.setState({ positiveInputVisible: true })}>
              <PlusOutlined />
              {newPositiveKeywordValue || 'New Positive Keyword'}
            </Tag>
            )}
          </Form.Item>
          <Form.Item name="negativeKeywords" label="Negative Keywords">
            {[...new Set(negativeKeywords)].map((keyword) => (
              <Tag
                color="red"
                key={`negative-${keyword}`}
                style={{ marginTop: '10px' }}
                closable={true}
                onClose={() => this.removeKeyword(keyword, 'negativeKeywords')}
              >
                {keyword}
              </Tag>
            ))}
            {negativeInputVisible && (
            <Input
              ref={(negativeInput) => { if (negativeInput != null) { negativeInput.focus(); } }}
              name="negativeKeywordInput"
              type="text"
              size="small"
              style={{ width: '45%', marginTop: '10px' }}
              value={newNegativeKeywordValue}
              onChange={(e) => this.setState({ newNegativeKeywordValue: e.target.value })}
              onPressEnter={() => this.addNewKeyword('negativeKeywords')}
              disabled={disableButton}
            />
            )}
            {!negativeInputVisible && (
            <Tag style={{ marginTop: '10px' }} onClick={() => this.setState({ negativeInputVisible: true })}>
              <PlusOutlined />
              {newNegativeKeywordValue || 'New Negative Keyword'}
            </Tag>
            )}
          </Form.Item>
        </div>
        )}
        {isDepartmentLabelsEnabled && (
        <Form.Item
          name="assign labels"
          label={(
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Tooltip placement="left" title="Labels will be assigned to a department otherwise labels will be assigned to the organization by default">
                Department Labels
                <InfoCircleOutlined style={{ paddingLeft: '2px', marginTop: '6px', height: '8px' }} />
              </Tooltip>
            </div>
              )}
        >
          <Checkbox
            checked={labelsCanBeAssignedDepartment}
            disabled={disableButton}
            onChange={(event) => this.setState((prevState) => ({
              labelsCanBeAssignedDepartment: event.target.checked,
              assignedDepartment: event.target.checked ? prevState.assignedDepartment : null,
            }))}
          >
            Assign label to a department
          </Checkbox>
          <br />
          <br />
          {labelsCanBeAssignedDepartment && userDepartments.length > 0 && (
          <Radio.Group onChange={(event) => this.setState({ assignedDepartment: event.target.value })} value={assignedDepartment}>
            <Space direction="vertical">
              {userDepartments.map((department) => (
                <Radio value={department.id} key={department.id}>{department.name}</Radio>
              ))}
            </Space>
          </Radio.Group>
          )}
          {labelsCanBeAssignedDepartment && userDepartments.length === 0 && (
          <p><i>Currently you don&apos;t have any departments assigned to you.</i></p>
          )}
        </Form.Item>
        )}
      </Form>
    );
  };

  render() {
    const { isLoading, hideModal, modalType: { data: { isAdmin } } } = this.props;
    const { editingLabelID, canManageLabel } = this.state;
    const disableButton = canManageLabel && !isAdmin;
    const customContentStyle = {};

    return (
      <ModalWrapper
        wrapClassName="settings-modal-wrap"
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        width="600px"
        action={!editingLabelID ? this.addLabel : this.editLabel}
        actionName={editingLabelID ? 'Save' : 'Add'}
        dismiss={isLoading ? '' : 'Cancel'}
        showConfirmation={!!editingLabelID}
        confirmationTitle="Save changes to this label? Automatic labeling changes apply only to documents received after you save."
        disabled={disableButton || isLoading || !this.automaticRuleIsValid()}
        form="addLabel"
        modalTitle={editingLabelID ? 'Edit Label' : 'Add New Label'}
      >
        <div onKeyPress={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
        >
          {disableButton && (
            <div style={{ marginBottom: '25px' }}>
              <Alert
                message="You do not have permissions to add/edit/delete labels. Please contact your admin to make any changes."
                type="info"
                showIcon
              />
            </div>
          )}
          {this.renderModal()}
        </div>
      </ModalWrapper>
    );
  }
}

AddLabel.defaultProps = {
  error: false,
  errorCode: null,
};

AddLabel.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  getLabelsByPage: PropTypes.func.isRequired,
  updateLabel: PropTypes.func.isRequired,
  createNewLabel: PropTypes.func.isRequired,
  error: PropTypes.bool,
  errorCode: PropTypes.string,
};

export default connect((state) => ({
  isLoading: state.labels.isLoading,
  error: state.labels.error,
  errorCode: state.labels.errorCode,
}))(AddLabel);
